import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { compose } from 'recompose'
import { connect } from 'react-redux'
import { Redirect, useHistory } from 'react-router-dom'
import { isNil } from 'lodash'
import useAuth from 'components/auth/useAuth'
import { Aor } from 'components'
import {
  NextStepsContent,
  OnlineAccountNextSteps,
  OrderConfirmationContent,
  OrderSummary,
} from 'member-portal/components'
import { selectors as memberSelectors } from 'member-reducer'
import * as memberActions from 'member-actions'
import {
  AppliedDiscountCodeType,
  BillingType,
  hasNoAuth0Login,
  HEROKU_ID_KEY,
  MembershipType,
} from 'types'
import {
  makeMemberApplicationApplyPath,
  makeMemberPortalPath,
  PATH,
  NEXT_STEPS_CONTENT,
  ORDER_CONFIRMATION_CONTENT,
} from 'config'
import * as LS from 'local-storage'
import { formatUSMonthDayYearDateString } from 'utils'

const propTypes = {
  billing: BillingType,
  clearShoppingCart: PropTypes.func.isRequired,
  discountCodeDetails: AppliedDiscountCodeType,
  isTrialMembershipsActive: PropTypes.bool,
  membership: MembershipType,
  promoCodeIsTrial: PropTypes.bool,
  trialDuration: PropTypes.number,
}

function FinalConfirmationAndLogin({
  billing = null,
  clearShoppingCart,
  discountCodeDetails = null,
  isTrialMembershipsActive = false,
  membership = null,
  promoCodeIsTrial = false,
  trialDuration,
}) {
  const history = useHistory()
  const { isAuthenticated, login } = useAuth()

  // Snapshot on mount — clearShoppingCart wipes the LS entry before the Auth0
  // redirect. `hasNoAuth0Login` handles the "missing flag → has login"
  // polarity; the `!!existingContact` guard distinguishes a brand-new
  // applicant (no lookup ever happened) from a matched contact.
  const [hasExistingLogin] = useState(() => {
    if (isAuthenticated) return false
    const existingContact = LS.getExistingContactInfo()
    return !!existingContact && !hasNoAuth0Login(existingContact)
  })

  // Guard against direct navigation or partial membership state.
  if (isNil(membership) || isNil(membership?.membership_expiration_date__c)) {
    return <Redirect to={makeMemberApplicationApplyPath(PATH.PAYMENT)} />
  }

  const {
    aor,
    apple_pass__c: applePassUrl,
    google_pass__c: googlePassUrl,
    membership_expiration_date__c: membershipExpirationDate,
    membership_number__c: membershipNumber,
    membership_type__c: membershipType,
    [HEROKU_ID_KEY]: externalMembershipId,
  } = membership
  const formattedExpirationDate = formatUSMonthDayYearDateString(
    membershipExpirationDate,
  )

  const goToDashboard = () => {
    clearShoppingCart()
    history.push(makeMemberPortalPath(PATH.MEMBERSHIPS))
  }

  const loginAppState = {
    externalMembershipId,
    membershipExpirationDate,
    membershipNumber,
    returnTo: PATH.MEMBER_PORTAL,
  }

  const setupAccount = () => {
    clearShoppingCart()
    login({
      appState: loginAppState,
      authorizationParams: {
        prompt: 'login',
        screen_hint: 'signup',
      },
    })
  }

  const loginToAccount = () => {
    clearShoppingCart()
    login({
      appState: loginAppState,
      authorizationParams: {
        prompt: 'login',
      },
    })
  }

  const hasOnlineAccount = isAuthenticated || hasExistingLogin

  const getNextStepsContent = () => {
    if (isAuthenticated) {
      return {
        title: null,
        description:
          'To better serve you, ensure your boat information is complete\u2014add any missing details, extra vessels, and contacts as needed.',
        buttonText: 'Go to My Account',
        handler: goToDashboard,
      }
    }

    if (hasExistingLogin) {
      return {
        title: null,
        description:
          'To better serve you, log in to ensure your boat information is complete\u2014add any missing details, extra vessels, and contacts as needed.',
        buttonText: 'Log In',
        handler: loginToAccount,
      }
    }

    return {
      title: NEXT_STEPS_CONTENT.NEW_MEMBER.title,
      description: NEXT_STEPS_CONTENT.NEW_MEMBER.description,
      buttonText: NEXT_STEPS_CONTENT.NEW_MEMBER.buttonText,
      handler: setupAccount,
    }
  }

  const nextStepsContent = getNextStepsContent()

  return (
    <div className="form-with-aside-wrapper form-block-container">
      <div className="form-with-aside-block confirmation">
        <div className="form-with-aside">
          <div className="card card-medium">
            <OrderConfirmationContent
              isAutoRenewEnabled={billing?.autoRenewalAgreement}
              title={ORDER_CONFIRMATION_CONTENT.NEW_MEMBER.title}
              description={ORDER_CONFIRMATION_CONTENT.NEW_MEMBER.description}
              membershipNumber={membershipNumber}
              expirationDate={formattedExpirationDate}
              applePassUrl={applePassUrl}
              googlePassUrl={googlePassUrl}
            />
          </div>

          <div className="card card-medium">
            <OrderSummary
              billing={billing}
              discountCodeDetails={discountCodeDetails}
              isTrialMembershipsActive={isTrialMembershipsActive}
              membershipType={membershipType}
              promoCodeIsTrial={promoCodeIsTrial}
              trialDuration={trialDuration}
            />
          </div>

          <div className="card card-medium confirmation-card">
            {hasOnlineAccount ? (
              <OnlineAccountNextSteps
                buttonText={nextStepsContent.buttonText}
                confirmationHandler={nextStepsContent.handler}
                description={nextStepsContent.description}
              />
            ) : (
              <NextStepsContent
                boats_info={NEXT_STEPS_CONTENT.NEW_MEMBER.boats_info}
                buttonText={nextStepsContent.buttonText}
                confirmationHandler={nextStepsContent.handler}
                description={nextStepsContent.description}
                title={nextStepsContent.title}
              />
            )}
          </div>
        </div>

        <aside>
          <div className="card image-card captain">
            <Aor aor={aor} />
          </div>
        </aside>
      </div>
    </div>
  )
}

FinalConfirmationAndLogin.propTypes = propTypes

function mapStateToProps(state) {
  return {
    billing: memberSelectors.billing(state),
    membership: memberSelectors.membership(state),
    membershipPrices: memberSelectors.membershipPrices(state),
    discountCodeDetails: memberSelectors.discountCodeDetails(state),
    isTrialMembershipsActive: memberSelectors.isTrialMembershipsActive(state),
    promoCodeIsTrial: memberSelectors.promoCodeIsTrial(state),
    trialDuration: memberSelectors.trialDuration(state),
  }
}

const mapDispatchToProps = {
  clearShoppingCart: memberActions.clearShoppingCart,
}

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  FinalConfirmationAndLogin,
)
